import { useEffect, useRef } from 'react';
import { useViewportStore } from '../../stores/viewportStore';

interface GridOverlayProps {
  containerWidth: number;
  containerHeight: number;
  artboardWidth: number;
  artboardHeight: number;
}

export default function GridOverlay({ containerWidth, containerHeight, artboardWidth, artboardHeight }: GridOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { gridVisible, gridSize, zoom, panX, panY } = useViewportStore();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    try {
      ctx.setTransform(1, 0, 0, 1, 0, 0);

      if (!gridVisible) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        return;
      }

      if (!(zoom > 0) || !(gridSize > 0) || !(containerWidth > 0) || !(containerHeight > 0)) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        return;
      }

      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.floor(containerWidth * dpr));
      canvas.height = Math.max(1, Math.floor(containerHeight * dpr));
      canvas.style.width = `${containerWidth}px`;
      canvas.style.height = `${containerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      ctx.clearRect(0, 0, containerWidth, containerHeight);

      const startX = Math.max(0, Math.floor(-panX / zoom / gridSize) * gridSize);
      const startY = Math.max(0, Math.floor(-panY / zoom / gridSize) * gridSize);
      const endX = Math.min(artboardWidth, Math.ceil((containerWidth - panX) / zoom / gridSize) * gridSize);
      const endY = Math.min(artboardHeight, Math.ceil((containerHeight - panY) / zoom / gridSize) * gridSize);

      const majorInterval = gridSize * 10;
      const effectiveGridSize = gridSize * zoom;

      // Perf guard: at gridSize=1 with a large canvas, minor grid generates
      // 4000+ path segments per draw. The < 4 threshold skips minor lines when
      // they'd be sub-pixel anyway, preventing main-thread lock on toggle.
      if (effectiveGridSize < 4) {
        drawLines(ctx, startX, startY, endX, endY, majorInterval, zoom, panX, panY, 'rgba(255, 255, 255, 0.08)', 1);
      } else {
        drawLines(ctx, startX, startY, endX, endY, gridSize, zoom, panX, panY, 'rgba(255, 255, 255, 0.04)', 0.5);
        drawLines(ctx, startX, startY, endX, endY, majorInterval, zoom, panX, panY, 'rgba(255, 255, 255, 0.1)', 1);
      }
    } catch (err) {
      console.warn('[GridOverlay] draw skipped', err);
    }
  }, [gridVisible, gridSize, zoom, panX, panY, containerWidth, containerHeight, artboardWidth, artboardHeight]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none z-[1]"
      style={{
        width: containerWidth,
        height: containerHeight,
        display: gridVisible ? 'block' : 'none',
      }}
    />
  );
}

function drawLines(
  ctx: CanvasRenderingContext2D,
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  interval: number,
  zoom: number,
  panX: number,
  panY: number,
  color: string,
  lineWidth: number
) {
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.beginPath();

  const screenStartY = Math.max(0, startY * zoom + panY);
  const screenEndY = endY * zoom + panY;
  const screenStartX = Math.max(0, startX * zoom + panX);
  const screenEndX = endX * zoom + panX;

  for (let x = Math.ceil(startX / interval) * interval; x <= endX; x += interval) {
    const sx = x * zoom + panX;
    ctx.moveTo(sx, screenStartY);
    ctx.lineTo(sx, screenEndY);
  }

  for (let y = Math.ceil(startY / interval) * interval; y <= endY; y += interval) {
    const sy = y * zoom + panY;
    ctx.moveTo(screenStartX, sy);
    ctx.lineTo(screenEndX, sy);
  }

  ctx.stroke();
}
